// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import addReorderPadFilters from "@adl/App/Handlers/ReorderPad/AddReorderPadFilters";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getReorderPadDataViewProperty } from "@adl/App/Store/Selectors/ReorderPadSelectors";
import { color, xScreen } from "@adl/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import { SortOptionModel } from "@insite/client-framework/Types/ApiModels";
import { IconWrapper } from "@insite/mobius/Icon";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: getReorderPadDataViewProperty(state, "pagination"),
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    addReorderPadFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    select?: SelectPresentationProps;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    select: {
        labelProps: {
            size: 12,
            weight: "normal",
            css: css`
                line-height: 18px;
                padding: 7px 0;
                width: unset;
                min-width: 50px;
                text-align: left;
                ${xScreen("lte", "md")} {
                    padding: 0 0 3px 0;
                    width: 100%;
                }
                ${xScreen("lte", "sm")} {
                    font-weight: bold;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                width: 280px;
                align-items: center;
                ${xScreen("lte", "sm")} {
                    width: 100%;
                    border: 1px solid ${color("common.border")};
                    background: ${color("common.background")};
                    padding: 12px;
                }
            `,
            formInputWrapper: css`
                flex-grow: 1;
                ${IconWrapper} {
                    height: 27px;
                }
            `,
            inputSelect: css`
                height: 30px;
                border: 1px solid ${color("common.ui6")};
            `,
        },
    },
};

const styles = sortSelectStyles;

const ReorderPadSortSelect = ({ addReorderPadFilters, pagination, isTabletScreen }: Props) => {
    if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addReorderPadFilters({ sort: event.currentTarget.value });
    };

    return (
        <Select
            {...styles.select}
            label={translate("Sort by")}
            value={pagination.sortType}
            labelPosition={isTabletScreen ? "top" : "left"}
            onChange={onChangeSortHandler}
            data-test-selector="productListSortSelect"
        >
            {pagination.sortOptions.map((sortOption: SortOptionModel) => (
                <option key={sortOption.sortType} value={sortOption.sortType}>
                    {sortOption.displayName}
                </option>
            ))}
        </Select>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReorderPadSortSelect);
